import React from "react";
import { SectionBasedProduct } from "@brandclub/common-ui";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Suggestions, Suggestion } from "@/components/ai-elements/suggestion";
import { ConversationSkeleton } from "./ConversationSkeleton";
import { MessageListV2 } from "./MessageListV2";
import { PromptInputConcierge } from "./PromptInputConcierge";
import ConciergeWelcome from "./ConciergeWelcome";
import { ProductPreview } from "../types/conversation";
import { ConversationMessage } from "../types/conversation";
import { useConversationScrollDetection } from "../hooks/useConversationScrollDetection";
import { LightRays } from "@/components/ui/light-rays";

interface ConciergeChatAreaV2Props {
  // Config
  appName?: string;
  brandId: string | null;
  conversationId: string | null;

  // Conversation state
  conversationHistory: ConversationMessage[];
  productMetadataMap: Map<string, SectionBasedProduct>;
  streamingProductGroups: Map<number, string[]>;
  previewSearchQueries: string[];
  previewQueryProducts: Array<{
    query: string;
    queryIndex: number;
    products: ProductPreview[];
    totalResults: number;
  }>;
  followUpQuestions: string[];
  streamingFeaturedProduct: {
    product: SectionBasedProduct;
    question: string;
  } | null;

  // Streaming state
  isActivelyStreaming: boolean;
  streamingAIResponse: string;
  currentStatus: string;
  isWaitingForFollowUpQueries: boolean;

  // Loading state
  isLoadingConversationQuery: boolean;

  // Handlers
  handleSearch: (searchTerm: string) => void;
  onRetry?: (messageIndex: number) => Promise<void>;
  onUpdateFeedback?: (
    messageIndex: number,
    feedback: { type: "like" | "dislike"; timestamp: string } | null
  ) => void;

  // Input state
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
}

export const ConciergeChatAreaV2: React.FC<ConciergeChatAreaV2Props> = ({
  appName,
  brandId,
  conversationId,
  conversationHistory,
  productMetadataMap,
  streamingProductGroups,
  previewSearchQueries,
  previewQueryProducts,
  followUpQuestions,
  streamingFeaturedProduct,
  isActivelyStreaming,
  streamingAIResponse,
  currentStatus,
  isWaitingForFollowUpQueries,
  isLoadingConversationQuery,
  handleSearch,
  onRetry,
  onUpdateFeedback,
  searchInputValue,
  setSearchInputValue,
}) => {
  // Derived state
  const showWelcome =
    conversationHistory.length === 0 && !isLoadingConversationQuery;
  // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
  const showTypingIndicator = isActivelyStreaming && 
    !streamingAIResponse && 
    previewSearchQueries.length === 0 && 
    previewQueryProducts.length === 0;

  // Scroll-based suggestions animation
  const { showSuggestions, shouldShowSuggestions, bottomRef } =
    useConversationScrollDetection({
      followUpQuestions,
      isActivelyStreaming,
      conversationHistoryLength: conversationHistory.length,
      showWelcome,
      searchInputValue,
    });

  return (
    <div
      className="tw:relative tw:flex tw:flex-1 tw:flex-col"
      style={{ minHeight: 0 }}
    >
      <Conversation>
        <ConversationContent className="tw:flex tw:items-center tw:justify-center">
          {isLoadingConversationQuery ? (
            <ConversationSkeleton />
          ) : showWelcome ? (
            <ConciergeWelcome
              onSearch={handleSearch}
              brandId={brandId ? parseInt(brandId) : undefined}
            />
          ) : (
            <>
              <MessageListV2
                conversationId={conversationId}
                messages={conversationHistory}
                showTypingIndicator={showTypingIndicator}
                streamingAIResponse={streamingAIResponse}
                productMetadataMap={productMetadataMap}
                streamingProductGroups={streamingProductGroups}
                currentStatus={currentStatus}
                previewSearchQueries={previewSearchQueries}
                previewQueryProducts={previewQueryProducts}
                isWaitingForFollowUpQueries={isWaitingForFollowUpQueries}
                isActivelyStreaming={isActivelyStreaming}
                streamingFeaturedProduct={streamingFeaturedProduct}
                onSearch={handleSearch}
                onRetry={onRetry}
                onUpdateFeedback={onUpdateFeedback}
                bottomRef={
                  bottomRef as unknown as React.RefObject<HTMLDivElement>
                }
              />
            </>
          )}
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div
        className="tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4 md:tw:px-0"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        {/* Follow-up suggestions when conversation is active */}
        {shouldShowSuggestions && (
          <div
            className={`tw:mx-auto tw:w-full tw:max-w-[900px] tw:transition-all tw:duration-300 tw:ease-out ${
              showSuggestions
                ? "tw:opacity-100 tw:translate-y-0 tw:max-h-96 tw:pointer-events-auto"
                : "tw:opacity-0 tw:translate-y-2 tw:max-h-0 tw:overflow-hidden tw:pointer-events-none"
            }`}
            style={{
              transitionDelay: showSuggestions ? "100ms" : "0ms",
            }}
          >
            <Suggestions>
              {followUpQuestions.map((question) => (
                <Suggestion
                  key={question}
                  suggestion={question}
                  onClick={(suggestion) => setSearchInputValue(suggestion)}
                />
              ))}
            </Suggestions>
          </div>
        )}

        <PromptInputConcierge
          placeholder={
            conversationHistory.length > 0
              ? "Ask a follow up question"
              : "Ask anything"
          }
          value={searchInputValue}
          onChange={setSearchInputValue}
          onSubmit={handleSearch}
          disabled={isActivelyStreaming}
          isStreaming={isActivelyStreaming}
        />
      </div>
    </div>
  );
};
